/**
 * AdminLayout Component
 * 
 * Main layout component for admin dashboard.
 * Combines sidebar with main content area and handles responsive behavior.
 * Manages sidebar collapse state and mobile navigation.
 * 
 * @component
 * @example
 * <AdminLayout 
 *   user={adminUser} 
 *   currentRoute="/dashboard/customers" 
 *   onNavigate={handleNavigate}
 *   onLogout={handleLogout}
 * >
 *   <CustomerTable />
 * </AdminLayout>
 */

import React, { useState, useEffect } from 'react';
import SidebarLayout from '../SidebarLayout';
import SecondarySidebar, { SecondaryMenuItem } from '../../../common/SecondarySidebar';
import { AdminUser } from '../../../../types/admin-v2';
import './AdminLayout.scss';

interface AdminLayoutProps {
  /** Current admin user */
  user: AdminUser;
  /** Current active route */
  currentRoute: string;
  /** Navigation callback */
  onNavigate: (route: string) => void;
  /** Logout callback */
  onLogout?: () => void;
  /** Page title */
  pageTitle?: string;
  /** Page description */
  pageDescription?: string;
  /** Children components to render in main content */
  children: React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Header actions */
  headerActions?: React.ReactNode;
  /** Navigation buttons (Back/Next) to show below header */
  navigationActions?: React.ReactNode;
  /** Secondary sidebar component - can be ReactNode or object with title/menuItems */
  secondarySidebar?: React.ReactNode | {
    title: string;
    menuItems: SecondaryMenuItem[];
    activeItemId?: string;
  };
  /** Dynamic sub-items function - returns sub-items for a parent item based on current route */
  getDynamicSubItems?: (parentId: string, currentRoute: string, permissions?: any, userRole?: string, userCompanyPlan?: string) => Array<{
    id: string;
    label: string;
    route: string;
    icon?: string;
  }> | undefined;
  /** Remove admin-v2-main-content-area class from main content area */
  noMainContentAreaClass?: boolean;
  /** Hide page header */
  hideHeader?: boolean;
  /** Remove content padding */
  noContentPadding?: boolean;
  /** Remove admin-layout-v2 class */
  noLayoutClass?: boolean;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  pageTitle,
  pageDescription,
  children,
  className = '',
  headerActions,
  navigationActions,
  secondarySidebar,
  getDynamicSubItems,
  noMainContentAreaClass = false,
  hideHeader = false,
  noContentPadding = false,
  noLayoutClass = false
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle mobile detection and sidebar state
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigate = (route: string) => {
    onNavigate(route);
    // Auto-collapse sidebar on mobile after navigation
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  };

  const layoutClass = `${noLayoutClass ? '' : 'admin-layout-v2'} ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isMobile ? 'mobile' : ''} ${className}`.trim();

  return (
    <div className={layoutClass}>
      {/* Left Sidebar */}
      <SidebarLayout
        user={user}
        currentRoute={currentRoute}
        onNavigate={handleNavigate}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
        onLogout={onLogout}
        getDynamicSubItems={getDynamicSubItems}
      />

      {/* Secondary Sidebar removed - now using dynamic sub-items in main sidebar */}

      {/* Main Content Area */}
      <div className={noMainContentAreaClass ? '' : 'admin-v2-main-content-area'}>
        {/* Page Header */}
        {!hideHeader && (
          <div className="admin-v2-page-header">
            <div className="admin-v2-page-title-section">
              {pageTitle && <h1 className="admin-v2-page-title">{pageTitle}</h1>}
              {pageDescription && <p className="admin-v2-page-description">{pageDescription}</p>}
            </div>
            <div className="admin-v2-page-actions">
              {headerActions}
              <button
                className="admin-v2-sidebar-toggle-mobile"
                onClick={handleToggleSidebar}
                type="button"
                aria-label="Toggle sidebar"
              >
                ☰
              </button>
            </div>
          </div>
        )}

        {/* Navigation Actions (Back/Next buttons) */}
        {navigationActions && (
          <div className="admin-v2-navigation-actions">
            {navigationActions}
          </div>
        )}

        {/* Content */}
        <div className={`admin-v2-content-container ${noContentPadding ? 'admin-v2-content-container--no-padding' : ''}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
